// adminportal/src/components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console and any error reporting service
    console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className='min-h-screen flex items-center justify-center bg-slate-900'>
          <div className='max-w-md w-full mx-4'>
            <div className='bg-slate-800 rounded-lg shadow-xl p-8 text-center'>
              <div className='flex justify-center mb-6'>
                <div className='bg-red-500/20 p-4 rounded-full'>
                  <AlertTriangle className='h-12 w-12 text-red-400' />
                </div>
              </div>

              <h2 className='text-2xl font-bold text-slate-100 mb-4'>Something went wrong</h2>

              <p className='text-slate-400 mb-6'>
                An unexpected error occurred. Please try refreshing the page or contact support if
                the problem persists.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className='mb-6 p-4 bg-slate-700 rounded-lg text-left'>
                  <h3 className='text-sm font-medium text-red-400 mb-2'>Error Details:</h3>
                  <pre className='text-xs text-slate-300 whitespace-pre-wrap'>
                    {this.state.error.toString()}
                  </pre>
                  {this.state.errorInfo && (
                    <details className='mt-2'>
                      <summary className='text-xs text-slate-400 cursor-pointer'>
                        Component Stack
                      </summary>
                      <pre className='text-xs text-slate-300 whitespace-pre-wrap mt-1'>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className='flex flex-col sm:flex-row gap-3'>
                <button
                  onClick={this.handleRetry}
                  className='flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors'
                >
                  <RefreshCw className='h-4 w-4' />
                  Try Again
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className='px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors'
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;


